"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";

export interface PaletteItem {
    id: string;
    label: string;
    section: string;
    href?: string;
    keywords?: string;
    action?: "toggle-theme";
}

interface Props {
    posts: { slug: string; title: string }[];
    projects: { slug: string; title: string }[];
}

interface SlashCmd {
    cmd: string;
    descKey: string;
}

const SLASH: SlashCmd[] = [
    { cmd: "/help", descKey: "slash.help" },
    { cmd: "/time set day", descKey: "slash.day" },
    { cmd: "/time set night", descKey: "slash.night" },
    { cmd: "/summon creeper", descKey: "slash.creeper" },
    { cmd: "/kill", descKey: "slash.kill" },
    { cmd: "/seed", descKey: "slash.seed" },
    { cmd: "/weather clear", descKey: "slash.weatherClear" },
    { cmd: "/weather rain", descKey: "slash.weatherRain" },
    { cmd: "/weather thunder", descKey: "slash.weatherThunder" },
    { cmd: "/xp", descKey: "slash.xp" },
    { cmd: "/tp home", descKey: "slash.tpHome" },
    { cmd: "/tp projects", descKey: "slash.tpProjects" },
    { cmd: "/tp blog", descKey: "slash.tpBlog" },
    { cmd: "/tp labs", descKey: "slash.tpLabs" },
    { cmd: "/tp dog", descKey: "slash.tpDog" },
    { cmd: "/tp trails", descKey: "slash.tpTrails" },
    { cmd: "/clear", descKey: "slash.clear" },
];

type Weather = "clear" | "rain" | "thunder";

const TP_TARGETS: Record<string, string> = {
    home: "/",
    projects: "/projects",
    blog: "/blog",
    labs: "/labs",
    dog: "/dog",
    trails: "/trails",
};

const CREEPER_PIXELS: number[][] = [
    [0, 0, 0, 0, 0, 0, 0, 0],
    [0, 1, 1, 0, 0, 1, 1, 0],
    [0, 1, 1, 0, 0, 1, 1, 0],
    [0, 0, 0, 1, 1, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 0, 0],
    [0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 1, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0],
];

type FxState =
    | "idle"
    | "appear-full"
    | "boom-full"
    | "appear-face"
    | "fade-face";

function setTheme(mode: "light" | "dark"): void {
    document.documentElement.setAttribute("data-theme", mode);
    try {
        localStorage.setItem("theme", mode);
    } catch {}
}

const PARTICLE_COLORS = ["#62b04a", "#4a8a37", "#2a4a1e", "#0a0a0a", "#3a3a3a", "#ff9f43", "#ffd400", "#a0a0a0"];

function buildExplosionParticles() {
    const count = 36;
    return Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
        const speed = 180 + Math.random() * 240;
        const dx = Math.cos(angle) * speed;
        const dy = Math.sin(angle) * speed;
        const rot = Math.floor(Math.random() * 720 - 360);
        const size = 6 + Math.floor(Math.random() * 8);
        const color = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
        const delay = Math.floor(Math.random() * 60);
        return { dx, dy, rot, size, color, delay, key: i };
    });
}

function ExplosionParticles(): React.ReactElement {
    const [particles] = useState(buildExplosionParticles);
    return (
        <div className="creeper-particles" aria-hidden>
            {particles.map((p) => (
                <span
                    key={p.key}
                    style={
                        {
                            "--dx": `${p.dx}px`,
                            "--dy": `${p.dy}px`,
                            "--rot": `${p.rot}deg`,
                            width: p.size,
                            height: p.size,
                            background: p.color,
                            animationDelay: `${p.delay}ms`,
                        } as React.CSSProperties
                    }
                />
            ))}
        </div>
    );
}

function CreeperFx({ state }: { state: FxState }): React.ReactElement | null {
    if (state === "idle") return null;
    const faceOnly = state === "appear-face" || state === "fade-face";
    const wrapClass =
        state === "boom-full"
            ? "creeper-fx-boom"
            : state === "appear-full"
              ? "creeper-fx-appear"
              : state === "appear-face"
                ? "creeper-fx-face-in"
                : "creeper-fx-face-out";
    return (
        <div
            className={`pointer-events-none fixed inset-0 z-[70] flex items-center justify-center ${wrapClass}`}
            aria-hidden
        >
            {state === "boom-full" && <ExplosionParticles />}
            <div
                className="creeper-face grid"
                style={{
                    width: 240,
                    height: 240,
                    gridTemplateColumns: "repeat(8, 1fr)",
                    gridTemplateRows: "repeat(8, 1fr)",
                    imageRendering: "pixelated",
                    boxShadow: faceOnly ? "none" : "0 10px 40px rgba(0,0,0,0.35)",
                }}
            >
                {CREEPER_PIXELS.flatMap((row, y) =>
                    row.map((cell, x) => {
                        if (faceOnly) {
                            const color = cell === 1 ? "currentColor" : "transparent";
                            return <div key={`${x}-${y}`} style={{ background: color }} />;
                        }
                        const greenA = "#62b04a";
                        const greenB = "#4a8a37";
                        const color = cell === 1 ? "#0a0a0a" : (x + y) % 2 === 0 ? greenA : greenB;
                        return <div key={`${x}-${y}`} style={{ background: color }} />;
                    })
                )}
            </div>
        </div>
    );
}

function DeathOverlay({
    open,
    onRespawn,
    onTitleScreen,
}: {
    open: boolean;
    onRespawn: () => void;
    onTitleScreen: () => void;
}): React.ReactElement | null {
    if (!open) return null;

    return (
        <div
            className="mc-death-overlay fixed inset-0 z-[80] flex flex-col items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            aria-label="You died"
        >
            <h1 className="mc-death-title">You Died!</h1>
            <p className="mc-death-score mt-10">
                Score: <span className="mc-death-score-num">0</span>
            </p>
            <div aria-hidden className="mc-death-cross mt-8 mb-2">
                +
            </div>
            <div className="mc-death-btns flex w-full max-w-[420px] flex-col gap-2">
                <button type="button" onClick={onRespawn} className="mc-btn">
                    Respawn
                </button>
                <button type="button" onClick={onTitleScreen} className="mc-btn">
                    Title Screen
                </button>
            </div>
        </div>
    );
}

function SeedToast({ seed }: { seed: string | null }): React.ReactElement | null {
    if (!seed) return null;
    return (
        <div className="seed-toast pointer-events-none fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md border border-emerald-700/70 bg-zinc-900/95 px-4 py-2 font-mono text-xs text-emerald-300 shadow-xl">
            Seed: <span className="text-emerald-100">{seed}</span>
        </div>
    );
}

function makeBoltPath(): string {
    const startX = 15 + Math.random() * 70;
    const segments = 9 + Math.floor(Math.random() * 4);
    let x = startX;
    let y = 0;
    let d = `M${x.toFixed(2)},${y.toFixed(2)}`;
    for (let i = 0; i < segments; i++) {
        y += 100 / segments + (Math.random() - 0.5) * 3;
        x += (Math.random() - 0.5) * 10;
        d += ` L${x.toFixed(2)},${y.toFixed(2)}`;
    }
    return d;
}

function buildWeatherDrops(kind: Weather) {
    if (kind === "clear") return [];
    return Array.from({ length: 55 }, (_, i) => ({
        key: i,
        left: Math.random() * 100,
        delay: -Math.random() * 1.5,
        duration: 0.6 + Math.random() * 0.5,
        height: 12 + Math.random() * 14,
        opacity: 0.25 + Math.random() * 0.3,
    }));
}

const WeatherOverlay = memo(function WeatherOverlay({ kind }: { kind: Weather }): React.ReactElement | null {
    const drops = useMemo(() => buildWeatherDrops(kind), [kind]);

    const [strike, setStrike] = useState<{ id: number; path: string } | null>(null);
    useEffect(() => {
        if (kind !== "thunder") return;
        let alive = true;
        let tid: number | null = null;
        const tick = () => {
            const wait = 2800 + Math.random() * 3200;
            tid = window.setTimeout(() => {
                if (!alive) return;
                setStrike({ id: Date.now(), path: makeBoltPath() });
                window.setTimeout(() => alive && setStrike(null), 700);
                tick();
            }, wait);
        };
        tick();
        return () => {
            alive = false;
            if (tid !== null) window.clearTimeout(tid);
        };
    }, [kind]);

    if (kind === "clear") return null;

    return (
        <div className="weather-overlay" aria-hidden>
            {drops.map((d) => (
                <span
                    key={d.key}
                    className="rain-drop"
                    style={{
                        left: `${d.left}%`,
                        height: d.height,
                        opacity: d.opacity,
                        animationDelay: `${d.delay}s`,
                        animationDuration: `${d.duration}s`,
                    }}
                />
            ))}
            {strike && (
                <>
                    <div className="thunder-flash" />
                    <svg
                        key={strike.id}
                        className="thunder-bolt"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                    >
                        <path d={strike.path} className="thunder-bolt-halo" />
                        <path d={strike.path} className="thunder-bolt-glow" />
                        <path d={strike.path} className="thunder-bolt-core" />
                    </svg>
                </>
            )}
        </div>
    );
});

function XpBar({ value }: { value: number | null }): React.ReactElement | null {
    const [animatedPct, setAnimatedPct] = useState(0);
    useEffect(() => {
        let raf2: number | null = null;
        const raf1 = requestAnimationFrame(() => {
            setAnimatedPct(0);
            if (value !== null) {
                raf2 = requestAnimationFrame(() => setAnimatedPct(value % 100));
            }
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2 !== null) cancelAnimationFrame(raf2);
        };
    }, [value]);

    if (value === null) return null;
    const level = Math.floor(value / 100);
    return (
        <div className="xp-overlay" aria-hidden>
            <div className="xp-level">{level}</div>
            <div className="xp-bar">
                <div className="xp-fill" style={{ transform: `scaleX(${animatedPct / 100})` }} />
            </div>
        </div>
    );
}

function ChatToast({ msg }: { msg: string | null }): React.ReactElement | null {
    if (!msg) return null;
    return (
        <div className="mc-chat-toast" aria-hidden>
            <span>{"<sn4pe>"}</span> {msg}
        </div>
    );
}

function normalize(s: string): string {
    return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}

export default function CommandPalette({ posts, projects }: Props) {
    const t = useTranslations("palette");
    const [open, setOpen] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [animOpen, setAnimOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const router = useRouter();

    const [fx, setFx] = useState<FxState>("idle");
    const [dead, setDead] = useState(false);
    const [seed, setSeed] = useState<string | null>(null);
    const [weather, setWeather] = useState<Weather>("clear");
    const clearArmedRef = useRef<number>(0);
    const [clearArmed, setClearArmed] = useState(false);
    const clearTimerRef = useRef<number | null>(null);
    const chatMsgTimerRef = useRef<number | null>(null);

    useEffect(() => {
        const root = document.documentElement;
        if (weather === "clear") {
            delete root.dataset.weather;
        } else {
            root.dataset.weather = weather;
        }
        return () => {
            delete root.dataset.weather;
        };
    }, [weather]);
    const [xpValue, setXpValue] = useState<number | null>(null);
    const [chatMsg, setChatMsg] = useState<string | null>(null);
    // Cancels a still-pending clear so a fast second command can't cut the message short.
    const showChatMsg = (text: string, ms = 1600) => {
        if (chatMsgTimerRef.current !== null) window.clearTimeout(chatMsgTimerRef.current);
        setChatMsg(text);
        chatMsgTimerRef.current = window.setTimeout(() => {
            setChatMsg(null);
            chatMsgTimerRef.current = null;
        }, ms);
    };

    useEffect(() => {
        if (!dead && xpValue === null) return;
        const id = "mc-pixel-font";
        if (document.getElementById(id)) return;
        const link = document.createElement("link");
        link.id = id;
        link.rel = "stylesheet";
        link.href = "https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap";
        document.head.appendChild(link);
    }, [dead, xpValue]);

    const isSlash = query.startsWith("/");

    const NAV: PaletteItem[] = useMemo(
        () => [
            { id: "nav-home", label: t("navHomeLabel"), section: t("sectionNav"), href: "/" },
            { id: "nav-projects", label: t("navProjectsLabel"), section: t("sectionNav"), href: "/projects" },
            { id: "nav-blog", label: t("navBlogLabel"), section: t("sectionNav"), href: "/blog" },
            { id: "nav-labs", label: t("navLabsLabel"), section: t("sectionNav"), href: "/labs" },
            { id: "nav-rss", label: t("navRssLabel"), section: t("sectionNav"), href: "/rss.xml" },
        ],
        [t],
    );

    const ACTIONS: PaletteItem[] = useMemo(
        () => [
            { id: "act-theme", label: t("actToggleTheme"), section: t("sectionActions"), action: "toggle-theme", keywords: "theme dark light" },
            { id: "act-github", label: "GitHub", section: t("sectionExternal"), href: "https://github.com/sn4pe", keywords: "social" },
            { id: "act-twitter", label: "Twitter", section: t("sectionExternal"), href: "https://twitter.com/sn4pe_", keywords: "social x" },
            { id: "act-email", label: "Email", section: t("sectionExternal"), href: "mailto:hi@sn4pe.com", keywords: "contact" },
        ],
        [t],
    );

    const items = useMemo<PaletteItem[]>(() => {
        return [
            ...NAV,
            ...projects.map<PaletteItem>((p) => ({
                id: `proj-${p.slug}`,
                label: p.title,
                section: t("sectionProjects"),
                href: `/projects/${p.slug}`,
            })),
            ...posts.map<PaletteItem>((p) => ({
                id: `post-${p.slug}`,
                label: p.title,
                section: t("sectionPosts"),
                href: `/blog/${p.slug}`,
            })),
            ...ACTIONS,
        ];
    }, [NAV, ACTIONS, posts, projects, t]);

    const filtered = useMemo(() => {
        const q = normalize(query.trim());
        if (!q) return items;
        const tokens = q.split(/\s+/);
        return items.filter((it) => {
            const hay = normalize([it.label, it.section, it.keywords ?? ""].join(" "));
            return tokens.every((tok) => hay.includes(tok));
        });
    }, [items, query]);

    const slashFiltered = useMemo(() => {
        const q = query.toLowerCase();
        return SLASH.filter((s) => s.cmd.startsWith(q) || q.startsWith(s.cmd));
    }, [query]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const isMac = navigator.platform.toLowerCase().includes("mac");
            const cmdK = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k";
            if (cmdK) {
                e.preventDefault();
                setOpen((o) => !o);
                return;
            }
            if (e.key === "Escape" && open) setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        if (open) {
            const raf = requestAnimationFrame(() => {
                setActive(0);
                setQuery("");
                setMounted(true);
                requestAnimationFrame(() => setAnimOpen(true));
            });
            return () => cancelAnimationFrame(raf);
        }
        if (clearTimerRef.current !== null) {
            window.clearTimeout(clearTimerRef.current);
            clearTimerRef.current = null;
        }
        clearArmedRef.current = 0;
        const raf = requestAnimationFrame(() => {
            setAnimOpen(false);
            setClearArmed(false);
        });
        const tid = window.setTimeout(() => setMounted(false), 200);
        return () => {
            cancelAnimationFrame(raf);
            window.clearTimeout(tid);
        };
    }, [open]);

    useEffect(() => {
        if (!open || !mounted) return;
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try {
            el.setSelectionRange(el.value.length, el.value.length);
        } catch {}
    }, [open, mounted]);

    useEffect(() => {
        const el = listRef.current?.querySelector<HTMLLIElement>(`[data-index="${active}"]`);
        el?.scrollIntoView({ block: "nearest" });
    }, [active]);

    const [prevIsSlash, setPrevIsSlash] = useState(isSlash);
    if (prevIsSlash !== isSlash) {
        setPrevIsSlash(isSlash);
        setActive(0);
    }

    const runCreeperBoom = useCallback(() => {
        setFx("appear-full");
        window.setTimeout(() => {
            setFx("boom-full");
            window.setTimeout(() => setFx("idle"), 850);
        }, 450);
    }, []);

    const runFaceThemeSwitch = useCallback((mode: "light" | "dark") => {
        setFx("appear-face");
        window.setTimeout(() => {
            setTheme(mode);
            setFx("fade-face");
            window.setTimeout(() => setFx("idle"), 400);
        }, 450);
    }, []);

    const runSlash = useCallback(
        (cmdRaw: string) => {
            const c = cmdRaw.trim().toLowerCase();
            if (c === "/help") {
                setQuery("/");
                setActive(0);
                inputRef.current?.focus();
                return;
            }
            if (c === "/clear") {
                const now = Date.now();
                const armed = clearArmedRef.current;
                if (armed && now - armed < 10000) {
                    clearArmedRef.current = 0;
                    setClearArmed(false);
                    if (clearTimerRef.current !== null) {
                        window.clearTimeout(clearTimerRef.current);
                        clearTimerRef.current = null;
                    }
                    setOpen(false);
                    try {
                        localStorage.clear();
                    } catch {}
                    setChatMsg(t("clearDone"));
                    window.setTimeout(() => window.location.reload(), 700);
                    return;
                }
                clearArmedRef.current = now;
                setClearArmed(true);
                setQuery("");
                setActive(0);
                inputRef.current?.focus();
                if (clearTimerRef.current !== null) {
                    window.clearTimeout(clearTimerRef.current);
                }
                clearTimerRef.current = window.setTimeout(() => {
                    clearArmedRef.current = 0;
                    setClearArmed(false);
                    clearTimerRef.current = null;
                }, 10000);
                return;
            }
            setOpen(false);
            if (c === "/time set day") {
                runFaceThemeSwitch("light");
                return;
            }
            if (c === "/time set night") {
                runFaceThemeSwitch("dark");
                return;
            }
            if (c === "/summon creeper") {
                runCreeperBoom();
                return;
            }
            if (c === "/kill") {
                setDead(true);
                return;
            }
            if (c === "/seed") {
                const n = Math.floor(Math.random() * 2 ** 32) - 2 ** 31;
                setSeed(String(n));
                window.setTimeout(() => setSeed(null), 3000);
                return;
            }
            if (c === "/weather clear" || c === "/weather rain" || c === "/weather thunder") {
                const kind = c.split(" ")[1] as Weather;
                setWeather(kind);
                const descKey = `slash.weather${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
                showChatMsg(t(descKey));
                return;
            }
            if (c.startsWith("/xp")) {
                const arg = c.slice(3).trim();
                const parsed = arg.length === 0 ? 100 : parseInt(arg, 10);
                const xp = Number.isFinite(parsed) ? Math.max(0, Math.min(9999, parsed)) : 100;
                setXpValue(xp);
                window.setTimeout(() => setXpValue(null), 2800);
                return;
            }
            if (c.startsWith("/tp ")) {
                const slug = c.slice(4).trim();
                const path = TP_TARGETS[slug];
                if (path) {
                    showChatMsg(t("tpTeleported", { slug }));
                    router.push(path);
                } else {
                    showChatMsg(t("tpUnknown", { slug }));
                }
                return;
            }
        },
        [router, runCreeperBoom, runFaceThemeSwitch, t],
    );

    const runItem = useCallback(
        (it: PaletteItem) => {
            setOpen(false);
            if (it.action === "toggle-theme") {
                const cur = document.documentElement.getAttribute("data-theme");
                const next = cur === "dark" ? "light" : "dark";
                setTheme(next);
                return;
            }
            if (it.href) {
                if (it.href.startsWith("http") || it.href.startsWith("mailto:")) {
                    window.open(it.href, it.href.startsWith("mailto:") ? "_self" : "_blank");
                } else if (it.href.endsWith(".xml")) {
                    window.location.href = it.href;
                } else {
                    router.push(it.href);
                }
            }
        },
        [router],
    );

    const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const max = isSlash ? slashFiltered.length - 1 : filtered.length - 1;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => Math.min(max, a + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => Math.max(0, a - 1));
        } else if (e.key === "Tab" && isSlash) {
            e.preventDefault();
            const sug = slashFiltered[active];
            if (sug) setQuery(sug.cmd);
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (isSlash) {
                const exact = SLASH.find((s) => s.cmd === query.trim().toLowerCase());
                const target = exact ?? slashFiltered[active];
                if (target) runSlash(target.cmd);
            } else {
                const it = filtered[active];
                if (it) runItem(it);
            }
        }
    };

    let lastSection = "";

    return (
        <>
            {mounted && (
                <div
                    data-state={animOpen ? "open" : "closed"}
                    className="fixed inset-0 z-[60] flex items-start justify-center bg-foreground/20 px-4 pt-[15vh] backdrop-blur-sm transition-opacity duration-200 ease-[var(--ease-out)] data-[state=closed]:opacity-0 data-[state=open]:opacity-100"
                    onClick={() => setOpen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t("label")}
                >
                    <div
                        data-state={animOpen ? "open" : "closed"}
                        className={`w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl transition-[transform,opacity] duration-200 ease-[var(--ease-out)] data-[state=closed]:translate-y-2 data-[state=closed]:scale-95 data-[state=closed]:opacity-0 data-[state=open]:translate-y-0 data-[state=open]:scale-100 data-[state=open]:opacity-100 ${
                            isSlash
                                ? "border-emerald-700/60 bg-zinc-950 text-emerald-100"
                                : "border-border bg-card"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            className={`flex items-center gap-2 border-b px-3 ${
                                isSlash ? "border-emerald-700/40" : "border-border"
                            }`}
                        >
                            {isSlash ? (
                                <span aria-hidden className="font-mono text-sm text-emerald-400">
                                    {">"}
                                </span>
                            ) : (
                                <svg
                                    aria-hidden
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="h-4 w-4 text-muted-foreground"
                                >
                                    <circle cx="9" cy="9" r="6" />
                                    <path d="m17 17-3.5-3.5" />
                                </svg>
                            )}
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setActive(0);
                                }}
                                onKeyDown={onInputKey}
                                aria-label={t("label")}
                                placeholder={isSlash ? t("placeholderSlash") : t("placeholder")}
                                className={`flex-1 bg-transparent py-3 text-sm focus:outline-none ${
                                    isSlash
                                        ? "font-mono text-emerald-100 placeholder:text-emerald-700/60"
                                        : "text-foreground placeholder:text-muted-foreground/60"
                                }`}
                            />
                            <kbd
                                className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${
                                    isSlash
                                        ? "border-emerald-700/50 bg-zinc-900 text-emerald-300"
                                        : "border-border bg-muted text-muted-foreground"
                                }`}
                            >
                                esc
                            </kbd>
                        </div>

                        {clearArmed && (
                            <div className="flex items-center gap-2 border-b border-amber-700/50 bg-amber-950/40 px-3 py-2 font-mono text-[11px] text-amber-200">
                                <span aria-hidden>⚠</span>
                                <span>{t("clearArm")}</span>
                            </div>
                        )}

                        <ul
                            ref={listRef}
                            role="listbox"
                            className="max-h-80 overflow-y-auto py-1"
                        >
                            {isSlash ? (
                                <>
                                    {slashFiltered.length === 0 && (
                                        <li className="px-3 py-6 text-center font-mono text-sm text-emerald-700/70">
                                            {t("unknown")}
                                        </li>
                                    )}
                                    <div className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-emerald-700/80">
                                        {t("sectionCommands")}
                                    </div>
                                    {slashFiltered.map((s, idx) => {
                                        const isActive = idx === active;
                                        return (
                                            <li
                                                key={s.cmd}
                                                data-index={idx}
                                                role="option"
                                                aria-selected={isActive}
                                                onMouseEnter={() => setActive(idx)}
                                                onClick={() => runSlash(s.cmd)}
                                                className={`mx-1 flex cursor-pointer items-center justify-between rounded-md px-2 py-2 font-mono text-sm ${
                                                    isActive
                                                        ? "bg-emerald-900/60 text-emerald-50"
                                                        : "text-emerald-200/90"
                                                }`}
                                            >
                                                <span className="truncate">{s.cmd}</span>
                                                <span className="ml-2 text-[10px] text-emerald-500/80">
                                                    {t(s.descKey)}
                                                </span>
                                            </li>
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    {filtered.length === 0 && (
                                        <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                                            {t("empty")}
                                        </li>
                                    )}
                                    {filtered.map((it, idx) => {
                                        const showSection = it.section !== lastSection;
                                        lastSection = it.section;
                                        const isActive = idx === active;
                                        return (
                                            <div key={it.id}>
                                                {showSection && (
                                                    <div className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                                                        {it.section}
                                                    </div>
                                                )}
                                                <li
                                                    data-index={idx}
                                                    role="option"
                                                    aria-selected={isActive}
                                                    onMouseEnter={() => setActive(idx)}
                                                    onClick={() => runItem(it)}
                                                    className={`mx-1 flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm ${
                                                        isActive
                                                            ? "bg-accent text-accent-foreground"
                                                            : "text-foreground"
                                                    }`}
                                                >
                                                    <span className="truncate">{it.label}</span>
                                                    {it.href?.startsWith("http") && (
                                                        <span className="ml-2 text-xs text-muted-foreground">↗</span>
                                                    )}
                                                </li>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </ul>

                        <div
                            className={`flex items-center justify-between border-t px-3 py-2 text-[10px] ${
                                isSlash
                                    ? "border-emerald-700/40 bg-zinc-900/60 text-emerald-500/80"
                                    : "border-border bg-muted/40 text-muted-foreground"
                            }`}
                        >
                            <span>
                                <kbd className="font-mono">↑↓</kbd> {t("navigate")} ·{" "}
                                <kbd className="font-mono">↵</kbd> {isSlash ? t("execute") : t("go")}
                                {isSlash && (
                                    <>
                                        {" "}· <kbd className="font-mono">tab</kbd> {t("autocomplete")}
                                    </>
                                )}
                            </span>
                            <span>
                                <kbd className="font-mono">⌘K</kbd> {t("toggle")}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            <CreeperFx state={fx} />
            <DeathOverlay
                open={dead}
                onRespawn={() => window.location.reload()}
                onTitleScreen={() => {
                    setDead(false);
                    router.push("/");
                }}
            />
            <SeedToast seed={seed} />
            <WeatherOverlay kind={weather} />
            <XpBar value={xpValue} />
            <ChatToast msg={chatMsg} />
        </>
    );
}
